/**
 * «Другое» — свободная гостевая заявка (#79, гостевой дом).
 *
 * Один экран: поле «Комментарий» + «Отправить». Создаёт ОДНУ свободную заявку
 * (POST /v1/requests) без привязки к конкретной услуге. Категория — общая
 * (concierge, если есть в каталоге, иначе первая доступная): бэк требует
 * category_id, поэтому выбираем нейтральную, не выдумывая новую схему.
 * IDOR #329 — гость создаёт заявку только себе.
 *
 * Паттерны (BINDING): отдельный экран с «Назад» (.ta-back-btn), комментарий —
 * textarea (не select, стрелку не вешаем).
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Skeleton } from '@hsc/ui';
import {
  ServiceCategoryId,
  type ServiceRequestCreatedResponse,
} from '@hsc/types';
import { resolveGuestLocale } from '../../lib/locale.js';
import { Toast } from '../../shared/ui/Toast.js';
import { useServiceCatalog, useCreateServiceRequest } from '../catalog/useCatalog.js';

/** Предпочтительный код категории для свободной заявки. */
const PREFERRED_CODES = ['concierge', 'other', 'housekeeping'] as const;

function OtherForm({ categoryId }: { categoryId: string }): JSX.Element {
  const navigate = useNavigate();
  const [comment, setComment] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<ServiceRequestCreatedResponse | null>(null);
  const mutation = useCreateServiceRequest();

  const onSubmit = (): void => {
    setError(null);
    const text = comment.trim();
    mutation.mutate(
      {
        category_id: ServiceCategoryId(categoryId),
        title: 'Другое',
        ...(text !== '' && { description: text }),
      },
      {
        onSuccess: (resp) => setDone(resp),
        onError: (err) =>
          setError(
            err.message !== ''
              ? err.message
              : 'Не получилось отправить — попробуйте ещё раз.',
          ),
      },
    );
  };

  if (done) {
    return (
      <section data-testid="other-success">
        <Toast message="Заявка отправлена · принято в работу" />
        <Card>
          <h2 className="hsc-card__title">Готово!</h2>
          <p>Мы передали Вашу заявку отелю — следить за статусом можно в разделе «Мои заявки».</p>
          <div className="rq-actions">
            <Button data-testid="other-open" onClick={() => navigate(`/requests/${encodeURIComponent(done.id)}`)}>
              Открыть заявку
            </Button>
            <Button variant="ghost" onClick={() => navigate('/')}>
              На главную
            </Button>
          </div>
        </Card>
      </section>
    );
  }

  return (
    <section data-testid="other-page">
      <button type="button" className="ta-back-btn" onClick={() => navigate('/')} data-testid="other-back">
        <span className="ta-back-btn__chev" aria-hidden="true">
          ‹
        </span>{' '}
        Назад
      </button>
      <h1 className="hsc-page__title">Другое</h1>
      <p className="ta-muted">Опишите, что нужно — мы разберёмся и поможем.</p>

      {error !== null && (
        <div className="ta-error-summary" role="alert" data-testid="other-error">
          <strong>Не получилось отправить:</strong> {error}
        </div>
      )}

      <div className="ta-field">
        <label className="hsc-input__label" htmlFor="other-comment">
          Комментарий
        </label>
        <textarea
          id="other-comment"
          className="ta-textarea"
          rows={4}
          placeholder="Если хотите что-то добавить или не нашлось нужного пункта"
          data-testid="other-comment"
          value={comment}
          onChange={(e) => setComment(e.currentTarget.value)}
        />
      </div>

      <div className="rq-actions">
        <Button
          type="button"
          loading={mutation.isPending}
          disabled={comment.trim() !== '' ? undefined : true}
          onClick={onSubmit}
          data-testid="other-submit"
        >
          Отправить
        </Button>
      </div>
    </section>
  );
}

export function OtherRequestPage(): JSX.Element {
  const locale = useMemo(() => resolveGuestLocale(), []);
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch } = useServiceCatalog(locale);

  if (isLoading) {
    return (
      <section aria-busy="true" data-testid="other-skeleton">
        <Skeleton height={40} radius={12} />
        <div style={{ height: 12 }} />
        <Skeleton height={120} radius={14} />
      </section>
    );
  }

  if (isError || !data) {
    return (
      <section>
        <h1 className="hsc-page__title">Другое</h1>
        <Card>
          <p role="alert">Сейчас не получается открыть форму. Попробуйте ещё раз.</p>
          <div className="rq-actions">
            <Button variant="secondary" onClick={() => void refetch()}>
              Повторить
            </Button>
            <Button variant="ghost" onClick={() => navigate('/')}>
              На главную
            </Button>
          </div>
        </Card>
      </section>
    );
  }

  let category = undefined;
  for (const code of PREFERRED_CODES) {
    category = data.categories.find((c) => c.code === code);
    if (category) break;
  }
  if (!category) category = data.categories[0];

  if (!category) {
    return (
      <section data-testid="other-no-category">
        <button type="button" className="ta-back-btn" onClick={() => navigate('/')}>
          <span className="ta-back-btn__chev" aria-hidden="true">
            ‹
          </span>{' '}
          Назад
        </button>
        <h1 className="hsc-page__title">Другое</h1>
        <Card>
          <p>Каталог отеля ещё наполняется — обратитесь на ресепшен.</p>
        </Card>
      </section>
    );
  }

  return <OtherForm categoryId={category.id} />;
}
